import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import superUserDebugService from '@/services/superUserDebugService.ts';
import type {
  DebugCompany,
  DebugSessionsQuery,
  DebugSessionsSort,
} from '@/types/superUserDebug.ts';
import type { DebugFilterKey } from '@/hooks/useSuperUserDebugSessions.ts';

const ANY_VALUE = '__any__';
// Filtro por estado intencionalmente oculto en v1 — el lifecycle del enum
// SessionStatus tiene problemas de raíz (sesiones quedan colgadas en
// InProgress, Abandoned no se asigna nunca). Plan de rediseño en
// docs/agent-layer/plans/2026-04-28_session-lifecycle-states-redesign.md
// El query-param `status` sigue soportado por hook + backend para no romper
// URLs antiguas, pero no se expone en la UI.

interface Props {
  query: DebugSessionsQuery;
  onFilterChange: (key: DebugFilterKey, value: string | undefined) => void;
  onSortChange: (sort: DebugSessionsSort) => void;
  onReset: () => void;
}

export default function SuperUserDebugSessionsFilters({
  query,
  onFilterChange,
  onSortChange,
  onReset,
}: Props) {
  const { t } = useTranslation();
  const [companies, setCompanies] = useState<DebugCompany[]>([]);
  const [companiesError, setCompaniesError] = useState<string | null>(null);
  const [searchDraft, setSearchDraft] = useState(query.q ?? '');

  useEffect(() => {
    let cancelled = false;
    superUserDebugService
      .getCompanies()
      .then((list) => {
        if (!cancelled) setCompanies(list);
      })
      .catch((err: unknown) => {
        if (!cancelled) setCompaniesError(err instanceof Error ? err.message : 'Error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setSearchDraft(query.q ?? '');
  }, [query.q]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = searchDraft.trim();
    onFilterChange('q', trimmed.length > 0 ? trimmed : undefined);
  };

  return (
    <div className="space-y-3 rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-end gap-3">
        {/* Búsqueda libre */}
        <form onSubmit={submitSearch} className="flex-1 min-w-[220px]">
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            {t('superuser.debug.filters.search', 'Buscar (sim o email)')}
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="search"
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                placeholder={t('superuser.debug.filters.searchPlaceholder', 'nombre simulación · email')}
                className="pl-8"
              />
            </div>
            <Button type="submit" size="sm">
              {t('superuser.debug.filters.searchSubmit', 'Buscar')}
            </Button>
          </div>
        </form>

        {/* Compañía */}
        <div className="min-w-[180px]">
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            {t('superuser.debug.filters.company', 'Compañía')}
          </label>
          <Select
            value={query.companyId ?? ANY_VALUE}
            onValueChange={(v) => onFilterChange('companyId', v === ANY_VALUE ? undefined : v)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t('superuser.debug.filters.any', 'Cualquiera')} />
            </SelectTrigger>
            <SelectContent position="popper" sideOffset={4}>
              <SelectItem value={ANY_VALUE}>
                {t('superuser.debug.filters.any', 'Cualquiera')}
              </SelectItem>
              {companies.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {companiesError && (
            <p className="mt-1 text-xs text-destructive">{companiesError}</p>
          )}
        </div>

        {/* Sort */}
        <div className="min-w-[180px]">
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            {t('superuser.debug.filters.sort', 'Orden')}
          </label>
          <Select
            value={query.sort ?? 'StartedAtDesc'}
            onValueChange={(v) => onSortChange(v as DebugSessionsSort)}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent position="popper" sideOffset={4}>
              <SelectItem value="StartedAtDesc">
                {t('superuser.debug.filters.sortDesc', 'Más recientes')}
              </SelectItem>
              <SelectItem value="StartedAtAsc">
                {t('superuser.debug.filters.sortAsc', 'Más antiguas')}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        {/* Fecha desde */}
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            {t('superuser.debug.filters.dateFrom', 'Desde')}
          </label>
          <Input
            type="date"
            value={query.dateFrom?.slice(0, 10) ?? ''}
            onChange={(e) => onFilterChange('dateFrom', e.target.value ? `${e.target.value}T00:00:00Z` : undefined)}
          />
        </div>

        {/* Fecha hasta */}
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            {t('superuser.debug.filters.dateTo', 'Hasta')}
          </label>
          <Input
            type="date"
            value={query.dateTo?.slice(0, 10) ?? ''}
            onChange={(e) => onFilterChange('dateTo', e.target.value ? `${e.target.value}T23:59:59Z` : undefined)}
          />
        </div>

        <div className="ml-auto">
          <Button variant="ghost" size="sm" onClick={onReset}>
            <X className="mr-1 size-4" />
            {t('superuser.debug.filters.reset', 'Limpiar')}
          </Button>
        </div>
      </div>
    </div>
  );
}
